/**
 * Seva API composable
 * Provides methods for all Seva module API endpoints
 */
import { apiClient } from '@shared/utils/apiClient.js'

export function useSeva() {
  // ==================== DASHBOARD ====================
  const getDashboard = () => apiClient.get('/v1/seva/dashboard')

  // ==================== PATRONS ====================
  const listPatrons = (params) => apiClient.get('/v1/seva/patrons', { params })
  const getPatron = (id) => apiClient.get(`/v1/seva/patrons/${id}`)
  const createPatron = (data) => apiClient.post('/v1/seva/patrons', data)
  const updatePatron = (id, data) => apiClient.put(`/v1/seva/patrons/${id}`, data)
  const deletePatron = (id) => apiClient.delete(`/v1/seva/patrons/${id}`)

  // ==================== DONATIONS ====================
  const listDonations = (params) => apiClient.get('/v1/seva/donations', { params })
  const getDonation = (id) => apiClient.get(`/v1/seva/donations/${id}`)
  const createDonation = (data) => apiClient.post('/v1/seva/donations', data)
  const updateDonation = (id, data) => apiClient.put(`/v1/seva/donations/${id}`, data)
  const deleteDonation = (id) => apiClient.delete(`/v1/seva/donations/${id}`)

  // ==================== EXPENSE CATEGORIES ====================
  const listExpenseCategories = () => apiClient.get('/v1/seva/expense-categories')
  const createExpenseCategory = (data) => apiClient.post('/v1/seva/expense-categories', data)
  const updateExpenseCategory = (id, data) => apiClient.put(`/v1/seva/expense-categories/${id}`, data)

  // ==================== EXPENSES ====================
  const listExpenses = (params) => apiClient.get('/v1/seva/expenses', { params })
  const getExpense = (id) => apiClient.get(`/v1/seva/expenses/${id}`)
  const createExpense = (data) => apiClient.post('/v1/seva/expenses', data)
  const updateExpense = (id, data) => apiClient.put(`/v1/seva/expenses/${id}`, data)
  const deleteExpense = (id) => apiClient.delete(`/v1/seva/expenses/${id}`)

  // ==================== PROGRAMS ====================
  const listPrograms = (params) => apiClient.get('/v1/seva/programs', { params })
  const getProgram = (id) => apiClient.get(`/v1/seva/programs/${id}`)
  const createProgram = (data) => apiClient.post('/v1/seva/programs', data)
  const updateProgram = (id, data) => apiClient.put(`/v1/seva/programs/${id}`, data)

  // ==================== RECEIPT TYPES ====================
  const listReceiptTypes = (params) => apiClient.get('/v1/seva/receipt-types', { params })
  const getReceiptType = (id) => apiClient.get(`/v1/seva/receipt-types/${id}`)
  const createReceiptType = (data) => apiClient.post('/v1/seva/receipt-types', data)
  const updateReceiptType = (id, data) => apiClient.put(`/v1/seva/receipt-types/${id}`, data)

  // ==================== FUNDS ====================
  const listFunds = (params) => apiClient.get('/v1/seva/funds', { params })
  const createFund = (data) => apiClient.post('/v1/seva/funds', data)
  const updateFund = (id, data) => apiClient.put(`/v1/seva/funds/${id}`, data)

  // ==================== MEMBERSHIP TIERS ====================
  const listMembershipTiers = (params) => apiClient.get('/v1/seva/membership-tiers', { params })
  const createMembershipTier = (data) => apiClient.post('/v1/seva/membership-tiers', data)
  const updateMembershipTier = (id, data) => apiClient.put(`/v1/seva/membership-tiers/${id}`, data)

  // ==================== RECEIPTS (UNIFIED) ====================
  const listReceipts = (params) => apiClient.get('/v1/seva/receipts', { params })
  const getReceipt = (id) => apiClient.get(`/v1/seva/receipts/${id}`)
  const createReceipt = (data) => apiClient.post('/v1/seva/receipts', data)
  const generateReceiptPDF = (id) => apiClient.post(`/v1/seva/receipts/${id}/pdf`)
  const downloadReceiptPDF = (id) => apiClient.getBlob(`/v1/seva/receipts/${id}/download`)
  const sendReceiptEmail = (id) => apiClient.post(`/v1/seva/receipts/${id}/email`)

  // ==================== ACTIVITY LOG ====================
  const listActivityLogs = (params) => apiClient.get('/v1/seva/activity-log', { params })
  const upsertActivityLog = (data) => apiClient.post('/v1/seva/activity-log', data)

  // ==================== DAILY STATS ====================
  const listDailyStats = (params) => apiClient.get('/v1/seva/daily-stats', { params })
  const upsertDailyStat = (data) => apiClient.post('/v1/seva/daily-stats', data)

  // ==================== MESSAGE LOGS ====================
  const getMessageLogs = (params) => apiClient.get('/v1/seva/messages', { params })

  // ==================== SETTINGS ====================
  const getSettings = () => apiClient.get('/v1/seva/settings')
  const updateSettings = (data) => apiClient.put('/v1/seva/settings', data)
  const getKVSettings = () => apiClient.get('/v1/seva/settings/kv')
  const updateKVSettings = (data) => apiClient.put('/v1/seva/settings/kv', data)

  // ==================== BANK ACCOUNTS ====================
  const listBankAccounts = (params) => apiClient.get('/v1/seva/bank-accounts', { params })
  const createBankAccount = (data) => apiClient.post('/v1/seva/bank-accounts', data)
  const getBankAccount = (id) => apiClient.get(`/v1/seva/bank-accounts/${id}`)
  const updateBankAccount = (id, data) => apiClient.put(`/v1/seva/bank-accounts/${id}`, data)
  const getAccountBalances = () => apiClient.get('/v1/seva/bank-accounts/balances')

  // ==================== ACCOUNT TRANSFERS ====================
  const listTransfers = (params) => apiClient.get('/v1/seva/transfers', { params })
  const createTransfer = (data) => apiClient.post('/v1/seva/transfers', data)

  // ==================== BENEFICIARIES ====================
  const listBeneficiaries = (params) => apiClient.get('/v1/seva/beneficiaries', { params })
  const getBeneficiary = (id) => apiClient.get(`/v1/seva/beneficiaries/${id}`)
  const createBeneficiary = (data) => apiClient.post('/v1/seva/beneficiaries', data)
  const updateBeneficiary = (id, data) => apiClient.put(`/v1/seva/beneficiaries/${id}`, data)
  const deleteBeneficiary = (id) => apiClient.delete(`/v1/seva/beneficiaries/${id}`)

  // ==================== ENROLLMENTS ====================
  const listEnrollments = (params) => apiClient.get('/v1/seva/enrollments', { params })
  const createEnrollment = (data) => apiClient.post('/v1/seva/enrollments', data)
  const updateEnrollment = (id, data) => apiClient.put(`/v1/seva/enrollments/${id}`, data)

  // ==================== BANK RECONCILIATION ====================
  const importBankStatement = (accountId, formData) => apiClient.upload(`/v1/seva/bank-accounts/${accountId}/import-statement`, formData)
  const listStatementImports = (accountId) => apiClient.get(`/v1/seva/bank-accounts/${accountId}/imports`)
  const getReconciliation = (importId) => apiClient.get(`/v1/seva/bank-accounts/imports/${importId}/reconcile`)
  const matchStatementRow = (importId, data) => apiClient.post(`/v1/seva/bank-accounts/imports/${importId}/match`, data)
  const clearStatementMatch = (importId, rowId) => apiClient.delete(`/v1/seva/bank-accounts/imports/${importId}/match/${rowId}`)

  // ==================== COMMITMENTS ====================
  const listAllCommitments = (params) => apiClient.get('/v1/seva/commitments', { params })
  const listPatronCommitments = (patronId) => apiClient.get(`/v1/seva/patrons/${patronId}/commitments`)
  const createCommitment = (patronId, data) => apiClient.post(`/v1/seva/patrons/${patronId}/commitments`, data)
  const updateCommitment = (id, data) => apiClient.put(`/v1/seva/commitments/${id}`, data)
  const deleteCommitment = (id) => apiClient.delete(`/v1/seva/commitments/${id}`)

  // ==================== RENEWALS ====================
  const getUpcomingRenewals = (params) => apiClient.get('/v1/seva/renewals', { params })

  // ==================== REPORTS ====================
  const getReceiptPaymentStatement = (params) => apiClient.get('/v1/seva/reports/receipt-payment', { params })
  const exportForm10BD = (fiscalYear) => apiClient.get('/v1/seva/reports/form-10bd', {
    params: { fiscal_year: fiscalYear },
    responseType: 'blob',
  })
  const getForm10BESummary = (fiscalYear) => apiClient.get('/v1/seva/reports/form-10be/summary', { params: { fiscal_year: fiscalYear } })
  const downloadForm10BE = (fiscalYear, patronId) => apiClient.get('/v1/seva/reports/form-10be/download', { params: { fiscal_year: fiscalYear, patron_id: patronId }, responseType: 'blob' })
  const getApplicationTest = (fiscalYear) => apiClient.get('/v1/seva/reports/application-test', { params: { fiscal_year: fiscalYear } })
  const exportCAPackage = (fiscalYear) => apiClient.get('/v1/seva/reports/ca-export', { params: { fiscal_year: fiscalYear }, responseType: 'blob' })
  const getIncomeExpenditure = (fiscalYear) => apiClient.get('/v1/seva/reports/income-expenditure', { params: { fiscal_year: fiscalYear } })
  const getBalanceSheet = (fiscalYear) => apiClient.get('/v1/seva/reports/balance-sheet', { params: { fiscal_year: fiscalYear } })
  const runIntegrityCheck = (fiscalYear) => apiClient.get('/v1/seva/reports/integrity-check', { params: { fiscal_year: fiscalYear } })

  return {
    // Dashboard
    getDashboard,
    // Patrons
    listPatrons,
    getPatron,
    createPatron,
    updatePatron,
    deletePatron,
    // Donations
    listDonations,
    getDonation,
    createDonation,
    updateDonation,
    deleteDonation,
    // Expense Categories
    listExpenseCategories,
    createExpenseCategory,
    updateExpenseCategory,
    // Expenses
    listExpenses,
    getExpense,
    createExpense,
    updateExpense,
    deleteExpense,
    // Programs
    listPrograms,
    getProgram,
    createProgram,
    updateProgram,
    // Activity Log
    listActivityLogs,
    upsertActivityLog,
    // Daily Stats
    listDailyStats,
    upsertDailyStat,
    // Message Logs
    getMessageLogs,
    // Settings
    getSettings,
    updateSettings,
    getKVSettings,
    updateKVSettings,
    // Bank Accounts
    listBankAccounts,
    createBankAccount,
    getBankAccount,
    updateBankAccount,
    getAccountBalances,
    // Bank Reconciliation
    importBankStatement,
    listStatementImports,
    getReconciliation,
    matchStatementRow,
    clearStatementMatch,
    // Transfers
    listTransfers,
    createTransfer,
    // Beneficiaries
    listBeneficiaries,
    getBeneficiary,
    createBeneficiary,
    updateBeneficiary,
    deleteBeneficiary,
    // Enrollments
    listEnrollments,
    createEnrollment,
    updateEnrollment,
    // Commitments
    listAllCommitments,
    listPatronCommitments,
    createCommitment,
    updateCommitment,
    deleteCommitment,
    // Renewals
    getUpcomingRenewals,
    // Receipt Types
    listReceiptTypes,
    getReceiptType,
    createReceiptType,
    updateReceiptType,
    // Funds
    listFunds,
    createFund,
    updateFund,
    // Membership Tiers
    listMembershipTiers,
    createMembershipTier,
    updateMembershipTier,
    // Receipts (unified)
    listReceipts,
    getReceipt,
    createReceipt,
    generateReceiptPDF,
    downloadReceiptPDF,
    sendReceiptEmail,
    // Reports
    getReceiptPaymentStatement,
    exportForm10BD,
    getForm10BESummary,
    downloadForm10BE,
    getApplicationTest,
    exportCAPackage,
    getIncomeExpenditure,
    getBalanceSheet,
    runIntegrityCheck,
  }
}
